import type { CommonData } from '@/components/blockEditor/blockEditor.types';
import { CURRENT_SCHEMA_VERSION } from '@/components/blockEditor/blockEditor.types';

export function getDefaultCltOverviewPageOneData(): CommonData {
  return {
    version: CURRENT_SCHEMA_VERSION,
    blocks: [
      {
        id: 'description',
        type: 'ListItemsSection',
        props: {},
        children: {
          default: [
            {
              id: 'clt-overview-heading',
              type: 'EducationalSlideHeading',
              props: {
                title: 'What is a CLT?',
                variant: 'h3',
              },
            },
            {
              id: 'clt-overview-body',
              type: 'EditableEducationalSlideBody',
              props: {
                text: `A Charitable Lead Trust (CLT) is an irrevocable trust that makes regular payments to a designated charity for a specific number of years with the remainder going to the donor's heirs or other designated beneficiaries. There are two main payout options for CLTs: Charitable Lead Annuity Trusts (CLATs), which pay a fixed amount annually, and Charitable Lead Unitrusts (CLUTs), which pay a fixed percentage of the trust's value, revalued annually.

There are two primary types of CLTs:

- Grantor CLT: The donor retains certain trust powers in order to maintain grantor trust status and is responsible for paying taxes on its income. The donor can receive an upfront charitable income tax deduction.
- Non-Grantor CLT: This type of trust is treated as a separate tax entity. The trust pays taxes on its income, but it can take a charitable deduction for the payments made to charity. The donor does not receive an upfront income tax deduction.`,
              },
            },
          ],
        },
      },
      {
        id: 'benefits',
        type: 'ListItemsSection',
        props: {},
        children: {
          default: [
            {
              id: 'clt-overview-page-one-columns',
              type: 'TwoColumnLayout',
              props: {
                leftWidth: '50%',
                rightWidth: '50%',
                columnGap: 2,
                showDivider: true,
              },
              children: {
                leftColumn: [
                  {
                    id: 'clt-overview-benefits-heading',
                    type: 'EducationalSlideHeading',
                    props: {
                      title: 'Potential benefits',
                      variant: 'h2',
                    },
                  },
                  {
                    id: 'clt-benefit-income-tax',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Income tax reduction',
                      description:
                        'With a Grantor CLT, the donor receives an upfront charitable income tax deduction for the present value of lead payments. With a Non-Grantor CLT, the donor avoids income tax on trust earnings.',
                      iconType: 'check',
                      iconColor: 'PRIMARY',
                    },
                  },
                  {
                    id: 'clt-benefit-estate-tax',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Estate tax reduction',
                      description:
                        'CLTs reduce the size of the taxable estate, which can reduce future estate tax liability.',
                      iconType: 'check',
                      iconColor: 'PRIMARY',
                    },
                  },
                  {
                    id: 'clt-benefit-charitable-support',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Support for charitable causes',
                      description:
                        'A CLT makes annual payments to charity, offering a reliable income stream for causes you care about.',
                      iconType: 'check',
                      iconColor: 'PRIMARY',
                    },
                  },
                  {
                    id: 'clt-benefit-control',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Control over charitable gifting',
                      description:
                        "The donor decides which charities receive payments and for how long. The trust document also often allows the donor to change charitable beneficiaries during the trust's term.",
                      iconType: 'check',
                      iconColor: 'PRIMARY',
                    },
                  },
                ],
                rightColumn: [
                  {
                    id: 'clt-overview-drawbacks-heading',
                    type: 'EducationalSlideHeading',
                    props: {
                      title: 'Potential drawbacks',
                      variant: 'h2',
                    },
                  },
                  {
                    id: 'clt-drawback-irrevocability',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Irrevocability',
                      description:
                        'Once established, a CLT cannot be modified or revoked (except for potentially updating the charitable beneficiaries if allowed). The annual payments are irrevocably committed to charity, and the donor cannot reclaim these assets.',
                      iconType: 'flag',
                      iconColor: 'NEGATIVE',
                    },
                  },
                  {
                    id: 'clt-drawback-grantor-taxation',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Grantor CLT taxation',
                      description:
                        'The donor is taxed on all trust income every year. If the trust generates significant taxable income, this can create a net tax drag for the donor.',
                      iconType: 'flag',
                      iconColor: 'NEGATIVE',
                    },
                  },
                  {
                    id: 'clt-drawback-non-grantor-taxation',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Non-Grantor CLT taxation',
                      description:
                        'Gains are not immediately taxed to the donor, but are still taxed at the trust level—potentially at higher rates, since trusts hit top tax brackets quickly.',
                      iconType: 'flag',
                      iconColor: 'NEGATIVE',
                    },
                  },
                  {
                    id: 'clt-drawback-complexity',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Complexity & costs',
                      description:
                        'Creating and maintaining a CLT involves legal and administrative expenses, and the structure may require ongoing professional management.',
                      iconType: 'flag',
                      iconColor: 'NEGATIVE',
                    },
                  },
                ],
              },
            },
          ],
        },
      },
    ],
  };
}
